import { useTranslation } from 'react-i18next';
import { X, Minus, Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatTimeLocal, formatCurrency } from '@/lib/format';
import AddonSelector from './AddonSelector';
import type { AddonAvailability, AddonSelection } from '@/api/hooks/useAddons';

type BookingCardProps = {
  unitName: string;
  startTime: string;
  endTime: string;
  duration: number;
  price: number;
  currency: string;
  minDuration: number;
  maxDuration: number;
  canExtend: boolean;
  playerCount: number;
  minPlayers: number;
  maxPlayers: number;
  isPerPerson: boolean;
  addons: AddonAvailability[];
  addonSelections: AddonSelection[];
  onAddonChange: (selections: AddonSelection[]) => void;
  onIncrease: () => void;
  onDecrease: () => void;
  onPlayerCountChange: (count: number) => void;
  onBook: () => void;
  onCancel: () => void;
};

export default function BookingCard({
  unitName,
  startTime,
  endTime,
  duration,
  price,
  currency,
  minDuration,
  maxDuration,
  canExtend,
  playerCount,
  minPlayers,
  maxPlayers,
  isPerPerson,
  addons,
  addonSelections,
  onAddonChange,
  onIncrease,
  onDecrease,
  onPlayerCountChange,
  onBook,
  onCancel,
}: BookingCardProps) {
  const { t, i18n } = useTranslation();

  const displayPrice = isPerPerson ? price * playerCount : price;
  const addonCost = addonSelections.reduce((sum, sel) => {
    const item = addons.find((e) => e.id === sel.addonId);
    return sum + (item ? item.rentalPrice * sel.quantity : 0);
  }, 0);
  const totalPrice = displayPrice + addonCost;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur border-t shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="max-w-lg mx-auto space-y-3">
        {/* Unit name + price */}
        <div className="flex justify-between items-start">
          <div>
            <p className="font-semibold">{unitName}</p>
            <p className="text-sm text-muted-foreground">
              {formatTimeLocal(startTime, i18n.language)} – {formatTimeLocal(endTime, i18n.language)}
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono text-lg font-semibold">
              {formatCurrency(totalPrice, currency, i18n.language)}
            </p>
            {isPerPerson && playerCount > 1 && (
              <p className="text-xs text-muted-foreground">
                {t('booking.per_person', { price: formatCurrency(price, currency, i18n.language) })}
              </p>
            )}
          </div>
        </div>

        {/* Addon selector */}
        <AddonSelector
          addons={addons}
          selections={addonSelections}
          onChange={onAddonChange}
          currency={currency}
        />

        {/* Actions row */}
        <div className="flex items-center gap-3">
          {/* Cancel button */}
          <Button variant="outline" size="icon" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>

          {/* Book button */}
          <Button className="flex-1" size="lg" onClick={onBook}>
            {t('common.book_now')}
          </Button>

          {/* Duration controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={onDecrease}
              disabled={duration <= minDuration}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium min-w-[50px] text-center">
              {t('booking.duration_minutes', { count: duration })}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={onIncrease}
              disabled={duration >= maxDuration || !canExtend}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Player count selector — only for per-person pricing */}
        {isPerPerson && (
          <div className="flex items-center justify-between pt-1 border-t">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>{t('booking.players_label')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPlayerCountChange(playerCount - 1)}
                disabled={playerCount <= minPlayers}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <span className="text-sm font-medium min-w-[24px] text-center">
                {playerCount}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPlayerCountChange(playerCount + 1)}
                disabled={playerCount >= maxPlayers}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
